import { useReactiveVar } from '@apollo/client'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { DocumentTemplateView } from '@deltek/specification-client/core/Models/documentTemplate'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProductTypeProperty } from '@deltek/specification-client/core/Models/productTypeProperty'
import { ProjectNugget } from '@deltek/specification-client/core/Models/projectNugget'
import { RemoveProjectContentInput } from '@deltek/specification-client/core/Models/projectProductProperty'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import {
    TableList,
    isContentTeam,
    isOmWorkspaceUser,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { ProductFamilyResponse } from 'api/graphQL/types/projectElement.types'
import { processEnv } from 'components/configuration/environmentVariables'
import { ExecutionResult } from 'graphql/execution'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React from 'react'
import CustomTabs from '../Common/CustomTabs'
import { TabsProvider } from '../Common/TabsContext'
import { NormalizedDocumentTemplateViewItem } from '../Common/model'
import {
    flattenedtTextContentElements,
    groupTemplate,
    updatePartlessLevel,
} from '../Common/utils'
import { HandleContentItemUpdateParams } from 'shared/utils/workspaceUtils'

export interface FamilyAreaProps {
    productFamily?: ProductFamilyResponse
    partType: 'General' | 'Products' | 'Execution' | 'ContentGroup' | 'All'
    loading: boolean
    familyLoading: boolean
    showGlobalSnackbar?: (options?: any) => Promise<ExecutionResult<any>>
    handleContentItemUpdate: (params: HandleContentItemUpdateParams) => void
    handleDeleteProperty: (
        inputValues: Partial<RemoveProjectContentInput>,
        isArticle?: boolean
    ) => void
    getUpdatedNugget?: (isDTVToBeRetrieved: boolean) => void
    contentTeamFromStudioPage?: boolean
    isDraftMode?: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    contentItemNodes?: Partial<ContentItem>[]
    dtv: Partial<DocumentTemplateView> | null
    productTypeProperties: Partial<ProductTypeProperty>[]
    tableList?: Partial<TableList>[]
    tabSelected: number
    projectProductFamilyId?: string
    isAddProhibited?: boolean
    addQueueSib?: string[] | undefined
    elementIdOnEdit?: string | undefined
    isAddOngoing?: boolean
    isNewAddOnQueue?: boolean
    familyAddedProductTypes?: ProjectElementNode[]
}

export const FamilyArea: React.FC<FamilyAreaProps> = ({
    productFamily,
    partType,
    loading,
    familyLoading,
    handleContentItemUpdate,
    handleDeleteProperty,
    getUpdatedNugget,
    contentTeamFromStudioPage,
    isDraftMode,
    selectedTopic,
    contentItemNodes,
    dtv,
    productTypeProperties,
    tableList,
    tabSelected,
    projectProductFamilyId,
    isAddProhibited,
    addQueueSib,
    elementIdOnEdit,
    isAddOngoing,
    isNewAddOnQueue,
    familyAddedProductTypes,
}) => {
    const [generalTemplates, setGeneralTemplates] = React.useState<
        NormalizedDocumentTemplateViewItem
    >()
    const [productsTemplates, setProductsTemplates] = React.useState<
        NormalizedDocumentTemplateViewItem
    >()
    const [executionTemplates, setExecutionTemplates] = React.useState<
        NormalizedDocumentTemplateViewItem
    >()
    const [baseSpecifierNotes, setBaseSpecifierNotes] = React.useState<
        NormalizedDocumentTemplateViewItem[]
    >()
    const [normalizedTemplates, setNormalizedTemplates] = React.useState<
        NormalizedDocumentTemplateViewItem[]
    >([])
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const nuggets: Partial<ProjectNugget>[] = React.useMemo(
        () => [
            ...(productFamily?.nuggets ?? []),
            ...(productFamily?.projectNuggets ?? []),
        ],
        [productFamily]
    )

    // Gather all elements within allContentItems
    const allElements = React.useMemo(() => {
        return contentItemNodes
            ?.reduce((elements, element) => {
                return [
                    ...elements,
                    ...flattenedtTextContentElements(element.elements ?? []),
                ]
            }, [] as Partial<TextContentElement>[])
            .filter(
                (value, index, array) =>
                    array.findIndex((test) => test.id === value.id) === index
            )
    }, [contentItemNodes])

    const handleUpdateTemplate = React.useCallback(
        (
            template: NormalizedDocumentTemplateViewItem,
            retrieveChanges?: boolean,
            contentItemNodes?: Partial<ContentItem>[]
        ) => {
            handleContentItemUpdate({
                updatedContentItems: [template.contentItem ?? {}],
                retrieveTrackedChanges: retrieveChanges,
                contentItemNodesInner: contentItemNodes,
            })
        },
        [handleContentItemUpdate]
    )

    const handleUpdateTemplateContentItems = React.useCallback(
        (contentItems: ContentItem[]) => {
            if (!!contentItems?.length) {
                handleContentItemUpdate({
                    updatedContentItems: contentItems,
                    retrieveTrackedChanges: true,
                })
            }
        },
        [handleContentItemUpdate]
    )

    // Based on the template, create a parent child object
    // Assume that null parentIds are the grandparents
    React.useEffect(() => {
        const newTemplateItems = groupTemplate(
            dtv ?? {},
            dtv?.items ?? [],
            null,
            contentItemNodes ?? ([] as Partial<ContentItem>[]),
            allElements ?? [],
            nuggets,
            0,
            contentTeamFromStudioPage ? false : true, // excludeProductType
            productFamily?.id ?? '',
            undefined,
            undefined,
            productTypeProperties,
            undefined,
            undefined,
            isOMUser,
            isOMUser && contentTeamFromStudioPage,
            isContentTeamUser && contentTeamFromStudioPage,
            tableList,
            familyAddedProductTypes,
            processEnv.REACT_APP_HideGroupSustainabilityContentInSMM !== '1' // temporary flag
        )

        if (
            newTemplateItems.length > 0 &&
            ((!newTemplateItems[0].isGeneral &&
                !newTemplateItems[0].isProduct &&
                !newTemplateItems[0].isExecution) ||
                ((partType === 'General' || partType === 'ContentGroup') &&
                    !newTemplateItems?.some(
                        (template) => template.isGeneral
                    )) ||
                (partType === 'Products' &&
                    !newTemplateItems?.some(
                        (template) => template.isProduct
                    )) ||
                (partType === 'Execution' &&
                    !newTemplateItems?.some(
                        (template) => template.isExecution
                    )))
        ) {
            const updatedPartlessTemplate = updatePartlessLevel(
                newTemplateItems
            )
            //Added placeholder partLess template parent
            const partLessTemplate: NormalizedDocumentTemplateViewItem = {
                id: '091521',
                children: [...updatedPartlessTemplate],
                contentItem: {},
                isGeneral: true,
                isExecution: false,
                isProduct: false,
                level: 0,
                documentTemplateView: {},
                documentTemplateViewItem: {},
                parentType: 'Nugget',
                productTypePropertyId: '',
                templateViewItemId: '',
                parentKey: '',
                familyId: '',
                productListings: undefined,
                isUserAdded: false,
                articleId: undefined,
            }

            setNormalizedTemplates([partLessTemplate])
        } else {
            setNormalizedTemplates(newTemplateItems)
        }
    }, [
        productFamily,
        allElements,
        nuggets,
        productTypeProperties,
        dtv,
        partType,
        contentItemNodes,
        isOMUser,
        isContentTeamUser,
        contentTeamFromStudioPage,
        tableList,
        processEnv,
    ])

    React.useEffect(() => {
        if (normalizedTemplates.length > 0) {
            const newGeneralTemplates = normalizedTemplates.find(
                (templateItem) => templateItem.isGeneral
            )
            const newProductsTemplates = normalizedTemplates.find(
                (templateItem) => templateItem.isProduct
            )
            const newExecutionTemplates = normalizedTemplates.find(
                (templateItem) => templateItem.isExecution
            )
            const newBaseSpecifierNotes = normalizedTemplates.filter(
                (each) => each.partType === 'BaseSpecifierNote'
            )

            setGeneralTemplates(newGeneralTemplates)
            setProductsTemplates(newProductsTemplates)
            setExecutionTemplates(newExecutionTemplates)
            setBaseSpecifierNotes(newBaseSpecifierNotes)
        }
    }, [normalizedTemplates])

    return (
        <TabsProvider
            haveProducts={productFamily?.hasProductType}
            handleUpdateTemplate={handleUpdateTemplate}
            handleUpdateTemplateContentItems={handleUpdateTemplateContentItems}
            getUpdatedNugget={getUpdatedNugget}
            removeProperty={handleDeleteProperty}
        >
            <CustomTabs
                baseNotes={baseSpecifierNotes}
                general={generalTemplates}
                products={productsTemplates}
                execution={executionTemplates}
                loading={familyLoading || loading}
                contentTeamFromStudioPage={contentTeamFromStudioPage}
                isDraftMode={isDraftMode}
                selectedTopic={selectedTopic}
                documentTemplateView={dtv}
                tab={tabSelected}
                projectProductFamilyId={projectProductFamilyId}
                isAddProhibited={isAddProhibited}
                dtv={dtv}
                addQueueSib={addQueueSib}
                contentItemNodes={contentItemNodes}
                elementIdOnEdit={elementIdOnEdit}
                isAddOngoing={isAddOngoing}
                isNewAddOnQueue={isNewAddOnQueue}
                familyAddedProductTypes={familyAddedProductTypes}
            />
        </TabsProvider>
    )
}

export default FamilyArea
